"use client";

import { ArrowRight } from "lucide-react";
import { useMemo, useState } from "react";
import type { AcademyLocationCard } from "@/features/academy-locations/queries";
import type { AcademyFormat } from "@/features/academy-locations/schema";
import type { PlaceSuggestion } from "@/features/places/search";
import { FORMAT_LABELS, kilometresBetween, SUGGEST_HREF } from "./format";
import LocationCard from "./LocationCard";
import LocationsMap from "./LocationsMap";
import PlaceSearch from "./PlaceSearch";

type Props = {
  locations: AcademyLocationCard[];
  /** Settings → Google Maps; null shows the search and list without a map. */
  mapsKey: string | null;
};

const PAGE = 6;

/**
 * The finder: a place search that sorts the locations by distance, a filter by
 * Academy format (only the formats some location has), the cards, and the map
 * when there's a key.
 */
export default function LocationFinder({ locations, mapsKey }: Props) {
  const [origin, setOrigin] = useState<PlaceSuggestion | null>(null);
  const [format, setFormat] = useState<AcademyFormat | null>(null);
  const [shown, setShown] = useState(PAGE);

  const formats = useMemo(
    () =>
      (Object.keys(FORMAT_LABELS) as AcademyFormat[]).filter((f) =>
        locations.some((location) => location.format === f),
      ),
    [locations],
  );

  const listed = useMemo(() => {
    const filtered = format
      ? locations.filter((location) => location.format === format)
      : locations;
    if (!origin)
      return filtered.map((location) => ({
        location,
        away: undefined as number | undefined,
      }));
    return filtered
      .map((location) => ({
        location,
        away:
          location.latitude !== null && location.longitude !== null
            ? kilometresBetween(origin, {
                latitude: location.latitude,
                longitude: location.longitude,
              })
            : undefined,
      }))
      .sort((a, b) => (a.away ?? Infinity) - (b.away ?? Infinity));
  }, [locations, format, origin]);

  return (
    <div className="shell py-28 tablet:py-22 mobile:py-16">
      <div className="mb-10 text-center">
        <p className="mb-3 font-das text-[13px] leading-[1.4] font-medium tracking-[0.14em] text-das-green uppercase">
          Find a DAS Academy
        </p>
        <h2 className="mb-0! font-das text-[52px] leading-[1.08] font-bold tracking-[-0.02em] text-das-black tablet:text-[42px] mobile:text-[34px]">
          Explore proposed DAS Academy locations
        </h2>
        <p className="mx-auto mt-5 mb-0 max-w-[64ch] font-das text-[19px] leading-[1.5] text-das-gray mobile:text-[17px]">
          {locations.length} proposed locations. Help bring a DAS Academy to
          your community.
        </p>
      </div>

      <div className="mx-auto mb-8 max-w-[640px]">
        <PlaceSearch
          chosen={origin}
          onChoose={(place) => {
            setOrigin(place);
            setShown(PAGE);
          }}
        />
      </div>

      {formats.length > 0 && (
        <div className="mx-auto mb-8 flex w-full max-w-[760px] gap-1 overflow-x-auto rounded-full bg-[#e8e8ed] p-1 [scrollbar-width:none]">
          {[null, ...formats].map((f) => (
            <button
              key={f ?? "all"}
              type="button"
              aria-pressed={format === f}
              onClick={() => {
                setFormat(f);
                setShown(PAGE);
              }}
              className={`h-11 flex-1 rounded-full px-5 text-[14px] font-medium whitespace-nowrap transition-colors mobile:text-[13px] ${format === f ? "bg-brand-blue text-white" : "text-das-black hover:bg-white/70"}`}
            >
              {f ? FORMAT_LABELS[f] : "All locations"}
            </button>
          ))}
        </div>
      )}

      {mapsKey && (
        <div className="mb-10">
          <LocationsMap
            apiKey={mapsKey}
            locations={listed.map(({ location }) => location)}
            origin={origin}
          />
        </div>
      )}

      <p className="mb-6 text-center text-[14px] text-das-gray">
        {origin
          ? `Nearest to ${origin.label}`
          : `${listed.length} of ${locations.length} proposed locations`}
      </p>

      <div className="grid grid-cols-3 gap-7 tablet:grid-cols-2 mobile:grid-cols-1">
        {listed.slice(0, shown).map(({ location, away }) => (
          <LocationCard key={location.id} location={location} away={away} />
        ))}
      </div>

      <div className="mt-10 flex flex-col items-center gap-4 text-center">
        {shown < listed.length && (
          <button
            type="button"
            onClick={() => setShown((n) => n + PAGE)}
            className="rounded-full border border-das-black/20 bg-white px-6 py-3 text-[15px] font-medium text-das-black hover:border-das-black/40"
          >
            Show more locations
          </button>
        )}
        <p className="text-[15px] text-das-gray">
          Your area isn&apos;t listed?{" "}
          <a
            href={SUGGEST_HREF}
            className="inline-flex items-center gap-1 font-medium text-blue-bright hover:underline"
          >
            Suggest a location <ArrowRight size={15} aria-hidden />
          </a>
        </p>
      </div>
    </div>
  );
}
